import { TranslateService } from '@ngx-translate/core';
import { RunPathEntry } from '../../models/test-run.model';

/**
 * Ein Glied des Ebenen-Pfads (NEX-2281): zuerst der Testfall, dann je umschliessendem
 * Modulaufruf das Modul mit Runde und Datenblattzeile. Lauf, Ergebnis und Protokoll bauen
 * denselben Text aus denselben Gliedern (NEX-2282), deshalb steht hier keine Komponente.
 */
export interface PathSegment {
  kind: 'case' | 'module';
  title: string | null;
  round: { phase: 'ROUND' | 'RETURN'; n: number | null } | null;
  row: number | null;
}

export type PathItem = { kind: 'segment'; segment: PathSegment; last: boolean } | { kind: 'ellipsis' };

export const PATH_SEPARATOR = ' › ';
export const PATH_ELLIPSIS = '…';
export const PATH_MAX_PARTS = 4;
const BADGE_SEPARATOR = ' · ';

function trimmed(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function roundOf(entry: RunPathEntry): PathSegment['round'] {
  if (entry.loopPhase === 'RETURN') return { phase: 'RETURN', n: entry.passIndex ?? null };
  return entry.passIndex != null ? { phase: 'ROUND', n: entry.passIndex } : null;
}

/** Testfall (Titel, sonst Schluessel) und die Module von aussen nach innen; ohne `path` nur der Testfall. */
export function pathSegments(
  caseTitle: string | null,
  caseKey: string | null,
  path: readonly RunPathEntry[] | null | undefined,
): PathSegment[] {
  const head: PathSegment = { kind: 'case', title: trimmed(caseTitle) ?? trimmed(caseKey), round: null, row: null };
  return [
    head,
    ...(path ?? []).map(
      (entry): PathSegment => ({
        kind: 'module',
        title: trimmed(entry.title),
        round: roundOf(entry),
        row: entry.iterationIndex ?? null,
      }),
    ),
  ];
}

/** Die Marke am Glied: „Runde 3", „Rückweg Runde 3", „Rückweg", „Zeile 4", „Runde 2 · Zeile 4" oder leer. */
export function segmentBadge(segment: PathSegment, translate: TranslateService): string {
  const parts: string[] = [];
  const round = segment.round;
  if (round?.phase === 'RETURN') {
    parts.push(
      round.n == null
        ? translate.instant('runPath.returnBlock')
        : translate.instant('runPath.returnRound', { n: round.n }),
    );
  } else if (round) {
    parts.push(translate.instant('runPath.round', { n: round.n }));
  }
  if (segment.row != null) parts.push(translate.instant('runPath.row', { n: segment.row }));
  return parts.join(BADGE_SEPARATOR);
}

/** Name des Glieds ohne Marke; fehlt er, steht der Ersatztext. */
export function segmentTitle(segment: PathSegment, translate: TranslateService): string {
  if (segment.title) return segment.title;
  return translate.instant(segment.kind === 'case' ? 'runPath.caseFallback' : 'runPath.untitledModule');
}

/** Das Glied als Text, die Marke in Klammern: „TM_X (Runde 3)". */
export function segmentText(segment: PathSegment, translate: TranslateService): string {
  const badge = segmentBadge(segment, translate);
  const title = segmentTitle(segment, translate);
  return badge ? `${title} (${badge})` : title;
}

/** Der volle Pfad als Text, etwa fuer Tooltip und Screenreader. */
export function pathText(segments: readonly PathSegment[], translate: TranslateService): string {
  return segments.map((segment) => segmentText(segment, translate)).join(PATH_SEPARATOR);
}

/** Mehr als `max` Glieder: das erste, „…" und die letzten zwei. Das letzte Glied ist markiert. */
export function shortenPath(segments: readonly PathSegment[], max = PATH_MAX_PARTS): PathItem[] {
  const item = (segment: PathSegment, index: number): PathItem => ({
    kind: 'segment',
    segment,
    last: index === segments.length - 1,
  });
  if (segments.length <= max) return segments.map(item);
  const tail = segments.length - 2;
  return [item(segments[0], 0), { kind: 'ellipsis' }, item(segments[tail], tail), item(segments[tail + 1], tail + 1)];
}
